/*! Cling Fishing engraving engine
 * Turns customer artwork, text and monograms into a one-tone laser mask.
 * mask: Uint8Array, 1 = engrave (shows white on the product), 0 = leave coating.
 */
(function (global) {
  'use strict';

  const FONTS = [
    { id: 'Oswald', weight: 600, label: 'Block' },
    { id: 'Roboto Slab', weight: 600, label: 'Slab' },
    { id: 'Playfair Display', weight: 700, label: 'Serif' },
    { id: 'Pacifico', weight: 400, label: 'Script' },
  ];
  const fontFor = (id) => FONTS.find((f) => f.id === id) || FONTS[0];

  function makeCanvas(w, h) {
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w));
    c.height = Math.max(1, Math.round(h));
    return c;
  }

  /* ---------- loading ---------- */
  // PDF and Illustrator support. PDF.js (Mozilla, Apache 2.0) loads only when someone uploads a PDF or AI
  // file. Old-style Illustrator and EPS files are PostScript, which browsers can't read; those go to the
  // proof server for conversion (configure({ convertUrl })).
  const OPTS = {
    pdfjsUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js',
    pdfjsWorkerUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js',
    convertUrl: null,
  };
  function configure(o) { Object.keys(o || {}).forEach((k) => { if (o[k] != null) OPTS[k] = o[k]; }); }
  let pdfjsPromise = null;
  function loadPdfJs() {
    if (global.pdfjsLib) return Promise.resolve(global.pdfjsLib);
    if (!pdfjsPromise) pdfjsPromise = new Promise((res, rej) => {
      const sc = document.createElement('script');
      sc.src = OPTS.pdfjsUrl; sc.async = true;
      sc.onload = () => {
        const lib = global.pdfjsLib || global['pdfjs-dist/build/pdf'];
        if (!lib) return rej(new Error('PDF reader failed to start'));
        lib.GlobalWorkerOptions.workerSrc = OPTS.pdfjsWorkerUrl;
        res(lib);
      };
      sc.onerror = () => { pdfjsPromise = null; rej(new Error("We couldn't open PDF files just now. Check your connection and try again, or upload a PNG or SVG.")); };
      document.head.appendChild(sc);
    });
    return pdfjsPromise;
  }
  // What kind of file is it really? (Names and types can't be trusted: an .ai file is usually a PDF inside.)
  async function sniff(file) {
    const b = new Uint8Array(await file.slice(0, 1024).arrayBuffer());
    const head = String.fromCharCode.apply(null, b.subarray(0, 16));
    if (head.startsWith('%PDF')) return 'pdf';
    if (head.startsWith('%!PS') || (b[0] === 0xc5 && b[1] === 0xd0 && b[2] === 0xd3 && b[3] === 0xc6)) return 'postscript';
    const asText = String.fromCharCode.apply(null, b);
    if (asText.indexOf('%PDF') >= 0) return 'pdf'; // a few tools put junk before the PDF header
    return 'image';
  }
  // Render page 1 of a PDF (or PDF-compatible Illustrator file) from its vectors, with a transparent
  // background. A quick low-resolution pass finds where the artwork sits on the page, then only that region
  // is rendered sharply, about 2400 px on its long side, so a small logo on a big artboard keeps its detail.
  async function loadPdf(file) {
    const lib = await loadPdfJs();
    const pdf = await lib.getDocument({ data: new Uint8Array(await file.arrayBuffer()), isEvalSupported: false }).promise;
    const page = await pdf.getPage(1);
    const v1 = page.getViewport({ scale: 1 });
    const s0 = 600 / Math.max(v1.width, v1.height);
    const vp0 = page.getViewport({ scale: s0 });
    const c0 = makeCanvas(vp0.width, vp0.height);
    await page.render({ canvasContext: c0.getContext('2d'), viewport: vp0, background: 'rgba(0,0,0,0)' }).promise;
    const d0 = c0.getContext('2d').getImageData(0, 0, c0.width, c0.height).data;
    let x0 = c0.width, y0 = c0.height, x1 = -1, y1 = -1;
    for (let y = 0; y < c0.height; y++) for (let x = 0; x < c0.width; x++) {
      if (d0[(y * c0.width + x) * 4 + 3] > 8) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    }
    if (x1 < 0) throw new Error("That file's first page is empty. Export your logo as a PNG or SVG and upload that instead.");
    const pad = 4; x0 = Math.max(0, x0 - pad); y0 = Math.max(0, y0 - pad); x1 = Math.min(c0.width - 1, x1 + pad); y1 = Math.min(c0.height - 1, y1 + pad);
    const bw = (x1 - x0 + 1) / s0, bh = (y1 - y0 + 1) / s0; // artwork size in PDF points
    const s1 = Math.min(40, 2400 / Math.max(bw, bh));
    const vp = page.getViewport({ scale: s1, offsetX: -(x0 / s0) * s1, offsetY: -(y0 / s0) * s1 });
    const c = makeCanvas(bw * s1, bh * s1);
    await page.render({ canvasContext: c.getContext('2d'), viewport: vp, background: 'rgba(0,0,0,0)' }).promise;
    return { canvas: c, naturalW: c.width, naturalH: c.height, vector: true, pages: pdf.numPages, kind: 'pdf' };
  }
  // PostScript (older Illustrator, EPS): converted to a high-resolution transparent PNG by the proof server.
  async function loadPostScript(file) {
    const msg = "This Illustrator or EPS file was saved in an older format we can't open here. In Illustrator, use File > Save As with \"Create PDF Compatible File\" checked, or export a PNG or SVG.";
    if (!OPTS.convertUrl) throw new Error(msg);
    const fd = new FormData(); fd.append('file', file, file.name);
    let res;
    try { res = await fetch(OPTS.convertUrl, { method: 'POST', body: fd }); } catch { throw new Error(msg); }
    if (!res.ok) throw new Error(msg);
    const png = await res.blob();
    const out = await loadRaster(new File([png], 'converted.png', { type: 'image/png' }));
    return Object.assign(out, { vector: true, kind: 'postscript' });
  }

  async function loadFile(file) {
    const kind = await sniff(file);
    if (kind === 'pdf') return loadPdf(file);
    if (kind === 'postscript') return loadPostScript(file);
    return loadRaster(file);
  }
  async function loadRaster(file) {
    const isSvg = file.type === 'image/svg+xml' || /\.svg$/i.test(file.name);
    let blob = file;
    if (isSvg) {
      const text = await file.text();
      const svg = new DOMParser().parseFromString(text, 'image/svg+xml').documentElement;
      if (!svg || svg.nodeName.toLowerCase() !== 'svg') throw new Error('That SVG file could not be read. Try exporting it as a PNG.');
      let w = parseFloat(svg.getAttribute('width'));
      let h = parseFloat(svg.getAttribute('height'));
      const vb = (svg.getAttribute('viewBox') || '').split(/[\s,]+/).map(Number);
      if (vb.length === 4 && vb[2] > 0 && vb[3] > 0) { w = vb[2]; h = vb[3]; }
      if (!(w > 0 && h > 0)) { w = 1000; h = 1000; }
      if (!svg.getAttribute('viewBox')) svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
      const s = 2400 / Math.max(w, h); // rasterize vectors large so edges stay crisp
      svg.setAttribute('width', Math.round(w * s));
      svg.setAttribute('height', Math.round(h * s));
      blob = new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml' });
    }
    const url = URL.createObjectURL(blob);
    try {
      const img = await new Promise((res, rej) => {
        const i = new Image();
        i.onload = () => res(i);
        i.onerror = () => rej(new Error("We couldn't open that file. Use a PNG, JPG, WEBP or SVG."));
        i.src = url;
      });
      const nw = img.naturalWidth || 1000;
      const nh = img.naturalHeight || 1000;
      const s = Math.min(1, 2400 / Math.max(nw, nh));
      const c = makeCanvas(nw * s, nh * s);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      return { canvas: c, naturalW: nw, naturalH: nh, vector: isSvg };
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  // Turn an already-loaded image (e.g. built-in artwork) into the same source object loadFile returns.
  function sourceFromImage(img, max = 2400) {
    const nw = img.naturalWidth || img.width, nh = img.naturalHeight || img.height;
    const s = Math.min(1, max / Math.max(nw, nh));
    const c = makeCanvas(nw * s, nh * s);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    return { canvas: c, naturalW: nw, naturalH: nh, vector: false };
  }

  /* ---------- analysis ---------- */
  // Luminance with transparency composited over white.
  function grayFrom(imgData) {
    const d = imgData.data, n = d.length >> 2, g = new Float32Array(n);
    for (let i = 0, j = 0; j < n; j++, i += 4) {
      const a = d[i + 3] / 255;
      g[j] = (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) * a + 255 * (1 - a);
    }
    return g;
  }

  function otsu(g) {
    const h = new Float64Array(256);
    for (let i = 0; i < g.length; i++) h[Math.min(255, Math.max(0, g[i] | 0))]++;
    let sum = 0;
    for (let i = 0; i < 256; i++) sum += i * h[i];
    let sB = 0, wB = 0, best = 0, t = 128;
    for (let i = 0; i < 256; i++) {
      wB += h[i];
      if (!wB) continue;
      const wF = g.length - wB;
      if (!wF) break;
      sB += i * h[i];
      const mB = sB / wB, mF = (sum - sB) / wF;
      const v = wB * wF * (mB - mF) * (mB - mF);
      if (v > best) { best = v; t = i; }
    }
    return t;
  }

  // Guess sensible starting settings so most customers never touch a slider.
  function analyze(src) {
    const s = Math.min(1, 400 / Math.max(src.width, src.height));
    const c = makeCanvas(src.width * s, src.height * s);
    const x = c.getContext('2d');
    x.drawImage(src, 0, 0, c.width, c.height);
    const id = x.getImageData(0, 0, c.width, c.height);
    const g = grayFrom(id), d = id.data, W = c.width, H = c.height;

    let clear = 0, opaqueSum = 0, opaqueN = 0, mid = 0;
    for (let j = 0; j < g.length; j++) {
      if (d[j * 4 + 3] < 128) { clear++; continue; }
      opaqueSum += g[j]; opaqueN++;
      if (g[j] > 40 && g[j] < 215) mid++;
    }
    const hasClear = clear / g.length > 0.05;
    const lightOnClear = hasClear && opaqueN > 0 && opaqueSum / opaqueN > 200;

    let t = otsu(g);
    let bs = 0, bn = 0;
    for (let xx = 0; xx < W; xx++) { bs += g[xx] + g[(H - 1) * W + xx]; bn += 2; }
    for (let y = 0; y < H; y++) { bs += g[y * W] + g[y * W + W - 1]; bn += 2; }
    const photoLike = !hasClear && opaqueN > 0 && mid / opaqueN > 0.35;
    // Photos already engrave their light areas, so a dark edge isn't a reason to swap.
    const darkBackground = !hasClear && !photoLike && bs / bn < t;

    if (lightOnClear) t = 128;
    return {
      threshold: Math.max(30, Math.min(225, t)),
      invert: lightOnClear || darkBackground,
      reason: lightOnClear ? 'light-on-clear' : darkBackground ? 'dark-background' : null,
      photoLike,
    };
  }

  /* ---------- masks ---------- */
  // Axis-aligned bounds of a rect rotated about its center by rect.rot (radians).
  function bounds(rect) {
    if (!rect.rot) return rect;
    const c = Math.abs(Math.cos(rect.rot)), s = Math.abs(Math.sin(rect.rot));
    const w = rect.w * c + rect.h * s, h = rect.w * s + rect.h * c;
    return { x: rect.x + rect.w / 2 - w / 2, y: rect.y + rect.h / 2 - h / 2, w, h };
  }
  // Run draw(ctx, w, h) with the context centered on rect and rotated by rect.rot.
  function withRect(x, rect, draw) {
    x.save();
    x.translate(rect.x + rect.w / 2, rect.y + rect.h / 2);
    if (rect.rot) x.rotate(rect.rot);
    if (rect.flip) x.scale(-1, 1); // mirror left-to-right
    draw(x, rect.w, rect.h);
    x.restore();
  }
  function clampRect(rect, W, H) {
    rect = bounds(rect);
    return {
      x0: Math.max(0, Math.floor(rect.x)), y0: Math.max(0, Math.floor(rect.y)),
      x1: Math.min(W, Math.ceil(rect.x + rect.w)), y1: Math.min(H, Math.ceil(rect.y + rect.h)),
    };
  }

  // opts: { mode: 'line'|'photo', threshold, invert, bright }
  function maskFromSource(src, W, H, rect, opts) {
    const c = makeCanvas(W, H), x = c.getContext('2d');
    x.imageSmoothingQuality = 'high';
    withRect(x, rect, (c, w, h) => c.drawImage(src, -w / 2, -h / 2, w, h));
    const id = x.getImageData(0, 0, W, H), d = id.data;
    const g = grayFrom(id), m = new Uint8Array(W * H);
    const { x0, y0, x1, y1 } = clampRect(rect, W, H);
    const inv = !!opts.invert;

    if (opts.mode === 'photo') {
      // Light areas engrave (they read white), shading becomes an Atkinson dot pattern.
      const k = 1.15, b = opts.bright || 0;
      for (let y = y0; y < y1; y++) for (let xx = x0; xx < x1; xx++) {
        const i = y * W + xx; g[i] = (g[i] - 128) * k + 128 + b;
      }
      for (let y = y0; y < y1; y++) {
        for (let xx = x0; xx < x1; xx++) {
          const i = y * W + xx;
          if (d[(i << 2) + 3] < 128) continue;
          const old = g[i], light = old >= 128, err = (old - (light ? 255 : 0)) / 8;
          m[i] = light !== inv ? 1 : 0;
          const spread = (px, py) => { if (px >= x0 && px < x1 && py < y1) g[py * W + px] += err; };
          spread(xx + 1, y); spread(xx + 2, y); spread(xx - 1, y + 1);
          spread(xx, y + 1); spread(xx + 1, y + 1); spread(xx, y + 2);
        }
      }
    } else {
      const t = opts.threshold == null ? 128 : opts.threshold;
      for (let y = y0; y < y1; y++) for (let xx = x0; xx < x1; xx++) {
        const i = y * W + xx;
        if (d[(i << 2) + 3] < 128) continue;
        m[i] = (g[i] < t) !== inv ? 1 : 0;
      }
    }
    return m;
  }

  function alphaMask(c) {
    const d = c.getContext('2d').getImageData(0, 0, c.width, c.height).data;
    const m = new Uint8Array(c.width * c.height);
    for (let i = 0; i < m.length; i++) m[i] = d[i * 4 + 3] >= 128 ? 1 : 0;
    return m;
  }

  function maskFromText(lines, fontId, W, H, rect) {
    const f = fontFor(fontId);
    const L = lines.map((s) => (s || '').trim()).filter(Boolean);
    if (!L.length) return { mask: new Uint8Array(W * H), fontPx: 0 };
    const c = makeCanvas(W, H), x = c.getContext('2d');
    x.font = `${f.weight} 100px "${f.id}", sans-serif`;
    const widest = Math.max(...L.map((s) => x.measureText(s).width)) || 1;
    const lh = 1.2;
    const size = Math.min((rect.w / widest) * 100 * 0.98, rect.h / (L.length * lh));
    withRect(x, rect, (cx) => {
      cx.font = `${f.weight} ${size}px "${f.id}", sans-serif`;
      cx.textAlign = 'center';
      cx.textBaseline = 'middle';
      cx.fillStyle = '#000';
      const top = -(L.length * size * lh) / 2;
      L.forEach((s, k) => cx.fillText(s, 0, top + size * lh * (k + 0.5)));
    });
    return { mask: alphaMask(c), fontPx: size };
  }

  // Traditional monogram: first initial, LAST initial (large, center), middle initial.
  function maskFromMonogram(mono, style, W, H, rect) {
    const first = (mono.first || '').trim().toUpperCase();
    const middle = (mono.middle || '').trim().toUpperCase();
    const last = (mono.last || '').trim().toUpperCase();
    const parts = style === 'block' ? [first, last, middle].filter(Boolean).map((t) => ({ t, s: 1 }))
      : [{ t: first, s: 0.62 }, { t: last, s: 1 }, { t: middle, s: 0.62 }].filter((p) => p.t);
    if (!parts.length) return { mask: new Uint8Array(W * H), fontPx: 0 };
    const f = fontFor(style === 'block' ? 'Oswald' : 'Playfair Display');
    const c = makeCanvas(W, H), x = c.getContext('2d');
    const gap = 0.06;
    x.font = `${f.weight} 100px "${f.id}", serif`;
    const widths = parts.map((p) => x.measureText(p.t).width * p.s);
    const totalAt100 = widths.reduce((a, b) => a + b, 0) + gap * 100 * (parts.length - 1);
    const size = Math.min((rect.w / totalAt100) * 100 * 0.98, rect.h * 0.9);
    withRect(x, rect, (g) => {
      g.textBaseline = 'middle';
      g.textAlign = 'left';
      g.fillStyle = '#000';
      let cx = -((totalAt100 * size) / 100) / 2;
      parts.forEach((p, k) => {
        g.font = `${f.weight} ${size * p.s}px "${f.id}", serif`;
        g.fillText(p.t, cx, 0);
        cx += (widths[k] * size) / 100 + gap * size;
      });
    });
    return { mask: alphaMask(c), fontPx: size };
  }

  // Width / height of the snug box around text or a monogram, so the on-screen handles hug the lettering.
  const measurer = (() => { let c; return () => (c = c || makeCanvas(10, 10).getContext('2d')); })();
  function textAspect(lines, fontId) {
    const f = fontFor(fontId), L = lines.map((s) => (s || '').trim()).filter(Boolean);
    if (!L.length) return 3;
    const x = measurer(); x.font = `${f.weight} 100px "${f.id}", sans-serif`;
    const widest = Math.max(...L.map((s) => x.measureText(s).width)) || 1;
    return (widest / 0.98) / (L.length * 1.2 * 100);
  }
  function monoAspect(mono, style) {
    const t = [mono.first, mono.last, mono.middle].map((s) => (s || '').trim().toUpperCase());
    const parts = style === 'block' ? t.filter(Boolean).map((v) => ({ t: v, s: 1 }))
      : [{ t: t[0], s: 0.62 }, { t: t[1], s: 1 }, { t: t[2], s: 0.62 }].filter((p) => p.t);
    if (!parts.length) return 1;
    const f = fontFor(style === 'block' ? 'Oswald' : 'Playfair Display'), x = measurer();
    x.font = `${f.weight} 100px "${f.id}", serif`;
    const total = parts.reduce((a, p) => a + x.measureText(p.t).width * p.s, 0) + 6 * (parts.length - 1);
    return (total / 0.98) / (100 / 0.9);
  }

  /* ---------- logo conversion: any artwork -> white engraving ----------
     Whatever colors, gradients or shading a customer's logo has, everything visible becomes white
     engraving; the background and any holes or negative space stay clear. Steps:
       1. analyzeLogo()  find the background (transparency, a solid border color, or none) and the
                         artwork's main colors, once per upload.
       2. labelLogo()    classify every pixel as background or one of those colors.
       3. renderLogo()   white where a chosen color is, clear elsewhere, optionally with a thin gap
                         between touching colors so multi-color logos stay recognizable. */
  const SRGB = new Float32Array(256);
  for (let i = 0; i < 256; i++) { const v = i / 255; SRGB[i] = v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); }
  const labF = (t) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  function lab(r, g, b) {
    const R = SRGB[r], G = SRGB[g], B = SRGB[b];
    const x = labF((R * 0.4124 + G * 0.3576 + B * 0.1805) / 0.95047);
    const y = labF(R * 0.2126 + G * 0.7152 + B * 0.0722);
    const z = labF((R * 0.0193 + G * 0.1192 + B * 0.9505) / 1.08883);
    return [116 * y - 16, 500 * (x - y), 200 * (y - z)];
  }
  const dE = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
  const hex = (c) => '#' + c.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('');

  // Working canvas for classifying pixels, always about TARGET px on its long side. Small or low-quality
  // files are enlarged with smooth interpolation first (up to 4x), so edges become gentle ramps that split
  // cleanly at sub-pixel accuracy, instead of hard steps that trace as jagged outlines. A light blur
  // (scaled to the enlargement) smooths away JPEG blocks and paper or canvas texture before classifying.
  const WORK_TARGET = 1600;
  const workCache = new WeakMap();
  function workCanvas(src) {
    if (workCache.has(src)) return workCache.get(src);
    const long = Math.max(src.width, src.height);
    const s = Math.min(4, WORK_TARGET / long);
    const c = makeCanvas(src.width * s, src.height * s), x = c.getContext('2d');
    x.imageSmoothingEnabled = true; x.imageSmoothingQuality = 'high';
    const blurPx = Math.max(0.6, 0.4 * s); // tuned on real customer files: enough to smooth JPEG blotches, light enough to keep hairlines
    let filtered = false;
    if ('filter' in x) { x.filter = `blur(${blurPx.toFixed(2)}px)`; filtered = x.filter !== 'none'; }
    if (s > 1) {
      // enlarge in steps of at most 2x: each step interpolates smoothly from the last
      let cur = src;
      while (cur.width * 2 < c.width) {
        const st = makeCanvas(cur.width * 2, cur.height * 2), sx = st.getContext('2d');
        sx.imageSmoothingEnabled = true; sx.imageSmoothingQuality = 'high'; sx.drawImage(cur, 0, 0, st.width, st.height); cur = st;
      }
      x.drawImage(cur, 0, 0, c.width, c.height);
    } else x.drawImage(src, 0, 0, c.width, c.height);
    if (!filtered) boxBlur(x, c.width, c.height, Math.max(1, Math.round(blurPx)));
    workCache.set(src, c);
    return c;
  }
  // Fallback blur for browsers without canvas filters.
  function boxBlur(x, W, H, r) {
    const id = x.getImageData(0, 0, W, H), d = id.data, t = new Float32Array(W * H * 4);
    for (let pass = 0; pass < 2; pass++) {
      const src = pass ? t : d, dst = pass ? d : t;
      for (let y = 0; y < H; y++) for (let xx = 0; xx < W; xx++) {
        let a0 = 0, a1 = 0, a2 = 0, a3 = 0, cnt = 0;
        for (let k = -r; k <= r; k++) {
          const X = pass ? xx : Math.min(W - 1, Math.max(0, xx + k)), Y = pass ? Math.min(H - 1, Math.max(0, y + k)) : y;
          const j = (Y * W + X) * 4; a0 += src[j]; a1 += src[j + 1]; a2 += src[j + 2]; a3 += src[j + 3]; cnt++;
        }
        const j = (y * W + xx) * 4; dst[j] = a0 / cnt; dst[j + 1] = a1 / cnt; dst[j + 2] = a2 / cnt; dst[j + 3] = a3 / cnt;
      }
    }
    x.putImageData(id, 0, 0);
  }

  function analyzeLogo(src) {
    const s = Math.min(1, 360 / Math.max(src.width, src.height));
    const c = makeCanvas(src.width * s, src.height * s), x = c.getContext('2d');
    x.drawImage(src, 0, 0, c.width, c.height);
    const d = x.getImageData(0, 0, c.width, c.height).data, W = c.width, H = c.height, n = W * H;

    // 1. background
    let clear = 0;
    for (let i = 0; i < n; i++) if (d[i * 4 + 3] < 128) clear++;
    let bgType = 'none', bg = null, bgShare = 0;
    if (clear / n > 0.03) bgType = 'alpha';
    else {
      // Sample a band around the edges (6% deep) and take its most common color. Using a band, not just
      // the outermost pixels, means a thin frame or border line drawn around the file doesn't fool it.
      const bandW = Math.max(3, Math.round(Math.min(W, H) * 0.06));
      const band = [];
      for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
        if (x >= bandW && x < W - bandW && y >= bandW && y < H - bandW) continue;
        if ((x + y) % 2) continue;
        band.push(y * W + x);
      }
      const labs = band.map((i) => lab(d[i * 4], d[i * 4 + 1], d[i * 4 + 2]));
      const bins = new Map();
      labs.forEach((l, k) => { const key = `${Math.round(l[0] / 6)},${Math.round(l[1] / 8)},${Math.round(l[2] / 8)}`; const b = bins.get(key); if (b) b.push(k); else bins.set(key, [k]); });
      const top = [...bins.values()].sort((a, b) => b.length - a.length)[0];
      const ref = [0, 1, 2].map((c) => top.reduce((a, k) => a + labs[k][c], 0) / top.length);
      const near = labs.map((l, k) => (dE(l, ref) < 12 ? k : -1)).filter((k) => k >= 0);
      bgShare = near.length / labs.length;
      if (bgShare >= 0.55) {
        bgType = 'color';
        bg = [0, 1, 2].map((c) => Math.round(near.reduce((a, k) => a + d[band[k] * 4 + c], 0) / near.length));
      }
    }
    const bgLab = bg ? lab(bg[0], bg[1], bg[2]) : null;

    // 2. artwork pixels and their colors. Only pixels inside solid areas count: blurred or JPEG-softened
    // edges are blends of two colors and would otherwise show up as extra colors or fake gradients.
    const LAB = new Array(n);
    for (let i = 0; i < n; i++) LAB[i] = lab(d[i * 4], d[i * 4 + 1], d[i * 4 + 2]);
    const interior = (i) => {
      const x = i % W, y = (i - x) / W;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const xx = x + dx, yy = y + dy; if (xx < 0 || yy < 0 || xx >= W || yy >= H) continue;
        if (dE(LAB[i], LAB[yy * W + xx]) > 7) return false;
      }
      return true;
    };
    const pts = [], artL = [];
    for (let i = 0; i < n; i++) {
      if (d[i * 4 + 3] < 128) continue;
      if (bgLab && dE(LAB[i], bgLab) < 18) continue;
      artL.push(LAB[i][0]);
      if (!interior(i)) continue;
      pts.push(LAB[i]);
    }
    // share of the artwork that's solid color (not edges, texture or shading): flat logos are mostly solid,
    // shaded illustrations mostly aren't
    const solidShare = pts.length / Math.max(1, artL.length);
    if (!pts.length) return { bgType, bg, bgLab, palette: [], flat: true, photoLike: false, artShare: 0 };

    // k-means on the artwork's colors, then merge near-duplicates and drop specks
    const k0 = Math.min(8, pts.length);
    // spread starting centers: farthest-point seeding
    let cen = [pts[0].slice()];
    while (cen.length < k0) {
      let best = 0, bi = 0;
      for (let i = 0; i < pts.length; i += 3) { const m = Math.min(...cen.map((c2) => dE(pts[i], c2))); if (m > best) { best = m; bi = i; } }
      if (best < 6) break;
      cen.push(pts[bi].slice());
    }
    const assign = new Int16Array(pts.length);
    for (let it = 0; it < 10; it++) {
      const sum = cen.map(() => [0, 0, 0, 0]);
      for (let i = 0; i < pts.length; i++) {
        let b = 0, bd = Infinity;
        for (let k = 0; k < cen.length; k++) { const v = dE(pts[i], cen[k]); if (v < bd) { bd = v; b = k; } }
        assign[i] = b; const S = sum[b]; S[0] += pts[i][0]; S[1] += pts[i][1]; S[2] += pts[i][2]; S[3]++;
      }
      cen = cen.map((c2, k) => (sum[k][3] ? [sum[k][0] / sum[k][3], sum[k][1] / sum[k][3], sum[k][2] / sum[k][3]] : c2));
    }
    let groups = cen.map((c2, k) => ({ lab: c2, count: assign.filter((a) => a === k).length }));
    groups.sort((a, b) => b.count - a.count);
    // merge close colors (anti-aliasing and JPEG noise create near-duplicates; two shades of one ink are one color)
    const mergeAt = (limit) => {
      const merged = [];
      for (const g of groups) {
        const m = merged.find((x2) => dE(x2.lab, g.lab) < limit);
        if (m) { const t = m.count + g.count; m.lab = m.lab.map((v, i) => (v * m.count + g.lab[i] * g.count) / t); m.count = t; }
        else merged.push({ ...g });
      }
      return merged.filter((g) => g.count / pts.length >= 0.02);
    };
    let palette = mergeAt(MERGE_DE);
    const finePalette = mergeAt(18);
    // Edge blends: anti-aliased pixels between two colors (or a color and the background) form their own
    // small cluster. Drop any small cluster that sits on the line between two others.
    const nearSegment = (p, a, b) => {
      const ab = [b[0] - a[0], b[1] - a[1], b[2] - a[2]], ap = [p[0] - a[0], p[1] - a[1], p[2] - a[2]];
      const L2 = ab[0] ** 2 + ab[1] ** 2 + ab[2] ** 2 || 1;
      const t = Math.max(0, Math.min(1, (ap[0] * ab[0] + ap[1] * ab[1] + ap[2] * ab[2]) / L2));
      return t > 0.08 && t < 0.92 && dE(p, [a[0] + ab[0] * t, a[1] + ab[1] * t, a[2] + ab[2] * t]) < 10;
    };
    palette = palette.filter((g) => {
      if (g.count / pts.length >= 0.15) return true;
      const anchors = palette.filter((o) => o !== g && o.count > g.count).map((o) => o.lab);
      if (bgLab) anchors.push(bgLab); else if (bgType === 'alpha') anchors.push([100, 0, 0], [0, 0, 0]);
      for (let a = 0; a < anchors.length; a++) for (let b = a + 1; b < anchors.length; b++) if (nearSegment(g.lab, anchors[a], anchors[b])) return false;
      return true;
    });
    // Gradients and shading: neighbouring artwork pixels differ by small amounts everywhere, while flat
    // artwork is uniform inside each color with sharp jumps only at edges.
    // Compare pixels STEP apart: a real gradient drifts steadily over that distance, while JPEG noise doesn't grow.
    const STEP = Math.max(4, Math.round(W / 14));
    let soft = 0, hard = 0;
    for (let y = 0; y < H; y += 2) for (let x = 0; x + STEP < W; x++) {
      const i = y * W + x, j = i + STEP;
      if (d[i * 4 + 3] < 128 || d[j * 4 + 3] < 128) continue;
      const e = dE(lab(d[i * 4], d[i * 4 + 1], d[i * 4 + 2]), lab(d[j * 4], d[j * 4 + 1], d[j * 4 + 2]));
      if (e < 3.5) continue;
      if (e < 14) soft++; else hard++;
    }
    const softShare = soft / Math.max(1, soft + hard);
    let onColor = 0;
    for (let i = 0; i < pts.length; i += 2) if (Math.min(...palette.map((g) => dE(pts[i], g.lab))) < 10) onColor++;
    let flat = onColor / Math.ceil(pts.length / 2) >= 0.8 && softShare < 0.72;
    // Drawn line work: three or more colors, one of them clearly darker than all the rest (the outlines and
    // drawing strokes of an illustration, or the leading of a stained-glass style logo). The dark line work is
    // what engraves; the colored fills stay clear. Checked before the gradient merge, because an illustration's
    // many colors and shading would otherwise read as one blended shape.
    let lineworkIdx = -1, lineworkOn = null, lineCut = null;
    const fineSep = finePalette.filter((g) => g.count / pts.length >= 0.02);
    if (fineSep.length >= 3) {
      const byDark = fineSep.map((g, i) => ({ i, L: g.lab[0], share: g.count / pts.length })).sort((a, b) => a.L - b.L);
      const dk = byDark[0], next = byDark[1];
      if (dk.L <= 45 && next.L - dk.L >= 18 && dk.share >= 0.05 && dk.share <= 0.65) {
        palette = fineSep; lineworkIdx = dk.i;
        if (solidShare >= 0.5) {
          // flat art (a stained-glass style logo, say): its darkest color is the line work; cut in the gap above it
          const cut = (dk.L + next.L) / 2;
          lineworkOn = palette.map((g) => g.lab[0] < cut);
        } else {
          // a shaded illustration: like a black-and-white trace, every pixel darker than a little past the art's
          // middle tone engraves, so the outlines and the darker shading strokes inside each color come through
          // (tuned on Clingfishing's finished files)
          const sorted = artL.slice().sort((a, b) => a - b), median = sorted[Math.floor(sorted.length / 2)];
          lineCut = Math.max(45, Math.min(75, median + 7));
          const dark = [0, 0, 0, 0], light = [0, 0, 0, 0];
          pts.forEach((p) => { const t = p[0] < lineCut ? dark : light; t[0] += p[0]; t[1] += p[1]; t[2] += p[2]; t[3]++; });
          const avg = (t) => (t[3] ? [t[0] / t[3], t[1] / t[3], t[2] / t[3]] : [50, 0, 0]);
          palette = [{ lab: avg(dark), count: dark[3], lineDark: true }, { lab: avg(light), count: light[3], lineLight: true }];
          lineworkIdx = 0; lineworkOn = [true, false];
        }
      }
    }
    if (lineworkIdx >= 0 && !flat) flat = true; // keep the colors separate: shading in the fills doesn't matter
    if (!flat) {
      // a gradient isn't a set of separate colors: treat all the artwork as one engraved shape
      const total = palette.reduce((a, g) => a + g.count, 0);
      palette = [{ lab: palette[0].lab, count: total, blended: true }];
    }
    // representative display color for each palette entry (closest real pixel)
    palette.forEach((g) => {
      let best = Infinity, rgb = [128, 128, 128];
      for (let i = 0; i < n; i += 2) {
        if (d[i * 4 + 3] < 128) continue;
        const v = dE(lab(d[i * 4], d[i * 4 + 1], d[i * 4 + 2]), g.lab);
        if (v < best) { best = v; rgb = [d[i * 4], d[i * 4 + 1], d[i * 4 + 2]]; }
      }
      g.hex = hex(rgb); g.share = g.count / pts.length;
    });
    const photoLike = bgType === 'none' && !flat;
    // Light linework on a dark fill (a classic badge or patch): the light lines are the design, and the dark
    // fill plays the part of the background. Engrave the light colors and leave the dark fill clear by default.
    let defaultOn = palette.map(() => true), lightOnDark = false;
    const linework = lineworkIdx >= 0 && lineworkIdx < palette.length;
    if (linework) defaultOn = lineworkOn || palette.map((g, i) => i === lineworkIdx);
    else if (palette.length > 1) {
      const byL = palette.map((g, i) => ({ i, L: g.lab[0], share: g.count / pts.length }));
      const dark = byL.reduce((a, b) => (a.L < b.L ? a : b)), light = byL.reduce((a, b) => (a.L > b.L ? a : b));
      if (dark.share >= 0.3 && light.L - dark.L >= 30 && (!bgLab || bgLab[0] < light.L)) {
        const mid = (dark.L + light.L) / 2;
        defaultOn = byL.map((c) => c.L > mid);
        lightOnDark = true;
      }
    }
    return { bgType, bg, bgLab, palette, flat, photoLike, softShare, defaultOn, lightOnDark, linework, lineCut, solidShare, artShare: pts.length / n, bgHex: bg ? hex(bg) : null };
  }

  /* Isolate the actual artwork: remove frames/borders drawn around the file and stray specks, then trim
     the empty margin so the design box fits the artwork itself. Returns a cleaned, cropped canvas
     (removed pixels become background) plus a summary of what was removed. */
  // colors closer than this are shades of one ink: merged, and never separated by a gap
  const MERGE_DE = 30;

  // A screenshot of a design program: the design sits on a big solid rectangle (the artboard) inside a window
  // of another color, often with labels like "Artboard 1". Find that rectangle: at least a quarter of the image,
  // straight edges, four sharp corners, a single color all around its border, and a margin of window around it.
  function findArtboard(L, comps, comp, W, H) {
    const n = W * H;
    let best = -1;
    comps.forEach((c, k) => { if (best < 0 || c.area > comps[best].area) best = k; });
    if (best < 0) return null;
    const c = comps[best], bw = c.x1 - c.x0 + 1, bh = c.y1 - c.y0 + 1;
    if (bw * bh < n * 0.25) return null;
    const margin = Math.max(2, Math.round(Math.min(W, H) * 0.01));
    const sides = [c.x0 >= margin, c.y0 >= margin, W - 1 - c.x1 >= margin, H - 1 - c.y1 >= margin].filter(Boolean).length;
    if (sides < 3) return null;
    // straight, filled edges and sharp corners
    const inC = (x, y) => comp[y * W + x] === best;
    let edge = 0, edgeN = 0;
    const labels = new Map();
    const tally = (x, y) => { edgeN++; if (inC(x, y)) { edge++; const l = L.lbl[y * W + x]; labels.set(l, (labels.get(l) || 0) + 1); } };
    for (let x = c.x0; x <= c.x1; x++) { tally(x, c.y0); tally(x, c.y1); }
    for (let y = c.y0; y <= c.y1; y++) { tally(c.x0, y); tally(c.x1, y); }
    if (edge / edgeN < 0.97) return null;
    const k2 = 2;
    for (const [x, y] of [[c.x0 + k2, c.y0 + k2], [c.x1 - k2, c.y0 + k2], [c.x0 + k2, c.y1 - k2], [c.x1 - k2, c.y1 - k2]]) if (!inC(x, y)) return null;
    // one color all around the border
    const top = [...labels.entries()].sort((a, b) => b[1] - a[1])[0];
    if (!top || top[1] / edge < 0.9) return null;
    return { x0: c.x0, y0: c.y0, x1: c.x1, y1: c.y1 };
  }

  function isolateArtwork(src, an, depth = 0) {
    if (an.bgType === 'none') return { canvas: src, frames: 0, specks: 0, trimmed: false };
    const L = labelLogo(src, an, 28), W = L.W, H = L.H, n = W * H, art = L.lbl;
    // connected pieces of artwork (8-connected)
    const comp = new Int32Array(n).fill(-1), comps = [];
    const stack = new Int32Array(n);
    for (let i = 0; i < n; i++) {
      if (!art[i] || comp[i] >= 0) continue;
      const id = comps.length; let sp = 0, area = 0, x0 = W, y0 = H, x1 = 0, y1 = 0;
      stack[sp++] = i; comp[i] = id;
      while (sp) {
        const j = stack[--sp], x = j % W, y = (j - x) / W; area++;
        if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y;
        for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx, yy = y + dy; if (xx < 0 || yy < 0 || xx >= W || yy >= H) continue;
          const k = yy * W + xx; if (art[k] && comp[k] < 0) { comp[k] = id; stack[sp++] = k; }
        }
      }
      comps.push({ area, x0, y0, x1, y1 });
    }
    // A screenshot's artboard: crop to it and start over, with the artboard's color as the background.
    if (depth === 0 && an.bgType === 'color') {
      const ab = findArtboard(L, comps, comp, W, H);
      if (ab) {
        const sx = src.width / W, sy = src.height / H, inset = 2;
        const cx0 = Math.ceil((ab.x0 + inset) * sx), cy0 = Math.ceil((ab.y0 + inset) * sy);
        const cw = Math.floor((ab.x1 - ab.x0 + 1 - inset * 2) * sx), ch = Math.floor((ab.y1 - ab.y0 + 1 - inset * 2) * sy);
        if (cw > 20 && ch > 20) {
          const cut = makeCanvas(cw, ch); cut.getContext('2d').drawImage(src, cx0, cy0, cw, ch, 0, 0, cw, ch);
          const inner = isolateArtwork(cut, analyzeLogo(cut), depth + 1);
          return Object.assign(inner, { artboard: true, trimmed: true });
        }
      }
    }
    // A frame runs along most of at least three edges of the file. Measure, for each piece, how much of
    // each edge (within a 3% band) it covers.
    const band = Math.max(2, Math.round(Math.min(W, H) * 0.03));
    const cover = comps.map(() => ({ t: new Uint8Array(W), b: new Uint8Array(W), l: new Uint8Array(H), r: new Uint8Array(H) }));
    const inBand = new Int32Array(comps.length);
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      const c = comp[y * W + x]; if (c < 0) continue;
      if (y < band || y >= H - band || x < band || x >= W - band) inBand[c]++;
      if (y < band) cover[c].t[x] = 1; if (y >= H - band) cover[c].b[x] = 1;
      if (x < band) cover[c].l[y] = 1; if (x >= W - band) cover[c].r[y] = 1;
    }
    const frac = (arr) => arr.reduce((a, v) => a + v, 0) / arr.length;
    const total = comps.reduce((a, c) => a + c.area, 0);
    const remove = new Uint8Array(comps.length);
    let frames = 0, specks = 0;
    comps.forEach((c, k) => {
      const sides = ['t', 'b', 'l', 'r'].filter((s2) => frac(cover[k][s2]) >= 0.5).length;
      // A frame is a thin outline: it runs along at least three edges AND most of it lies along the edges.
      // Artwork whose border touches the design inside it (a scene in a rounded box) is kept whole.
      if (sides >= 3 && inBand[k] / c.area >= 0.6) { remove[k] = 1; frames++; }
      // specks are tiny in absolute terms (JPEG dust, paper or canvas texture). Judge them against the image
      // size, not the amount of artwork, so skinny letters and small dots in detailed logos are never removed.
      else if (c.area < Math.max(6, n * 0.00002)) { remove[k] = 1; specks++; }
    });
    // bounds of what's left
    let x0 = W, y0 = H, x1 = -1, y1 = -1;
    comps.forEach((c, k) => { if (remove[k]) return; x0 = Math.min(x0, c.x0); y0 = Math.min(y0, c.y0); x1 = Math.max(x1, c.x1); y1 = Math.max(y1, c.y1); });
    if (x1 < 0) return { canvas: src, frames: 0, specks: 0, trimmed: false }; // nothing but frame: leave as is
    // map to source pixels, with a small margin
    const sx = src.width / W, sy = src.height / H, pad = Math.round(Math.max(x1 - x0, y1 - y0) * 0.02) + 2;
    const cx0 = Math.max(0, Math.floor((x0 - pad) * sx)), cy0 = Math.max(0, Math.floor((y0 - pad) * sy));
    const cx1 = Math.min(src.width, Math.ceil((x1 + 1 + pad) * sx)), cy1 = Math.min(src.height, Math.ceil((y1 + 1 + pad) * sy));
    const out = makeCanvas(cx1 - cx0, cy1 - cy0), ox = out.getContext('2d');
    ox.drawImage(src, cx0, cy0, out.width, out.height, 0, 0, out.width, out.height);
    if (frames || specks) {
      // paint removed pieces as background (transparent for transparent files)
      const id = ox.getImageData(0, 0, out.width, out.height), dd = id.data;
      const bg = an.bg ? an.bg.map(Math.round) : null;
      for (let y = 0; y < out.height; y++) for (let x = 0; x < out.width; x++) {
        const wx = Math.min(W - 1, Math.floor((x + cx0) / sx)), wy = Math.min(H - 1, Math.floor((y + cy0) / sy));
        // check a 3x3 neighbourhood at working resolution so anti-aliased edges of removed pieces go too
        let hit = false;
        for (let dy = -1; dy <= 1 && !hit; dy++) for (let dx = -1; dx <= 1 && !hit; dx++) {
          const xx = wx + dx, yy = wy + dy; if (xx < 0 || yy < 0 || xx >= W || yy >= H) continue;
          const c = comp[yy * W + xx]; if (c >= 0 && remove[c]) hit = true;
        }
        if (!hit) continue;
        const j = (y * out.width + x) * 4;
        if (bg) { dd[j] = bg[0]; dd[j + 1] = bg[1]; dd[j + 2] = bg[2]; dd[j + 3] = 255; } else dd[j + 3] = 0;
      }
      ox.putImageData(id, 0, 0);
    }
    const trimmed = out.width < src.width * 0.97 || out.height < src.height * 0.97;
    return { canvas: out, frames, specks, trimmed };
  }

  // Per-pixel labels at working resolution: 0 = background/clear, k+1 = palette color k.
  function labelLogo(src, an, bgTol) {
    const c = workCanvas(src), W = c.width, H = c.height, n = W * H;
    const d = c.getContext('2d').getImageData(0, 0, W, H).data;
    const lbl = new Uint8Array(n), P = an.palette.map((g) => g.lab);
    const cache = new Map();
    for (let i = 0; i < n; i++) {
      const j = i * 4;
      if (d[j + 3] < 128) continue;
      const key = (d[j] << 16) | (d[j + 1] << 8) | d[j + 2];
      let v = cache.get(key);
      if (v === undefined) {
        const L = lab(d[j], d[j + 1], d[j + 2]);
        let b = 0, bd = Infinity;
        for (let k = 0; k < P.length; k++) { const e = dE(L, P[k]); if (e < bd) { bd = e; b = k; } }
        if (an.lineCut != null) { b = L[0] < an.lineCut + (an.lineShift || 0) ? 0 : 1; bd = Math.min(bd, dE(L, P[b])); }
        // more background removal = the background color wins more of the blended pixels
        const dbg = an.bgLab ? dE(L, an.bgLab) * (28 / Math.max(5, bgTol)) : Infinity;
        v = !P.length ? (dbg < 28 ? 0 : 1) : dbg < bd ? 0 : b + 1;
        if (cache.size < 300000) cache.set(key, v);
      }
      lbl[i] = v;
    }
    // clean JPEG specks: a pixel takes the majority label of its 3x3 neighborhood
    const out = new Uint8Array(n);
    const cnt = new Uint8Array(256);
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      const i = y * W + x; let best = lbl[i], bc = 0;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const yy = y + dy, xx = x + dx; if (yy < 0 || yy >= H || xx < 0 || xx >= W) continue;
        const v = lbl[yy * W + xx]; if (++cnt[v] > bc) { bc = cnt[v]; best = v; }
      }
      out[i] = bc >= 5 ? best : lbl[i];
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) { const yy = y + dy, xx = x + dx; if (yy >= 0 && yy < H && xx >= 0 && xx < W) cnt[lbl[yy * W + xx]] = 0; }
    }
    return { W, H, lbl: out };
  }

  // White engraving canvas: chosen colors opaque white, everything else transparent.
  function renderLogo(L, colorsOn, gapR) {
    const { W, H } = L, art = logoMask(L, colorsOn, gapR), n = W * H;
    const c = makeCanvas(W, H), x = c.getContext('2d'), id = x.createImageData(W, H), d = id.data;
    for (let i = 0; i < n; i++) if (art[i]) { const j = i * 4; d[j] = d[j + 1] = d[j + 2] = d[j + 3] = 255; }
    x.putImageData(id, 0, 0);
    return c;
  }
  // 1 where the logo engraves: chosen colors, minus the thin gap where two colors touch.
  function logoMask(L, colorsOn, gapR) {
    const { W, H, lbl } = L, n = W * H;
    const on = (v) => v > 0 && colorsOn[v - 1] !== false;
    const art = new Uint8Array(n);
    for (let i = 0; i < n; i++) art[i] = on(lbl[i]) ? 1 : 0;
    if (gapR > 0) {
      // edges where two different engraved colors touch, widened to the gap width
      const edge = new Uint8Array(n);
      for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
        const i = y * W + x; if (!art[i]) continue;
        if ((x + 1 < W && art[i + 1] && lbl[i + 1] !== lbl[i]) || (y + 1 < H && art[i + W] && lbl[i + W] !== lbl[i])) edge[i] = 1;
      }
      const tmp = new Uint8Array(n);
      for (let y = 0; y < H; y++) { let run = -1e9; for (let x = 0; x < W; x++) { if (edge[y * W + x]) run = x; if (x - run <= gapR) tmp[y * W + x] = 1; } run = 1e9; for (let x = W - 1; x >= 0; x--) { if (edge[y * W + x]) run = x; if (run - x <= gapR) tmp[y * W + x] = 1; } }
      for (let x = 0; x < W; x++) { let run = -1e9; for (let y = 0; y < H; y++) { if (tmp[y * W + x]) run = y; if (y - run <= gapR) edge[y * W + x] = 1; } run = 1e9; for (let y = H - 1; y >= 0; y--) { if (tmp[y * W + x]) run = y; if (run - y <= gapR) edge[y * W + x] = 1; } }
      for (let i = 0; i < n; i++) if (edge[i]) art[i] = 0;
    }
    return art;
  }

  /* ---------- vector outlines ----------
     Traces a 1-bit mask into smooth closed outlines, so engraving can be drawn crisply at any size
     (and shown as a real SVG). Marching squares finds the boundary between engraved and clear pixels,
     two rounds of corner-cutting smooth out the pixel staircase, and near-duplicate points are dropped.
     Holes come out as their own loops; filling with the even-odd rule keeps them clear. */
  function traceMask(art, W, H) {
    const GW = W + 2; // grid padded by one clear pixel all round, so every outline closes
    const at = (x, y) => (x < 0 || y < 0 || x >= W || y >= H ? 0 : art[y * W + x]);
    // boundary points sit on the midpoints of grid edges; give each a unique id
    const hId = (x, y) => ((y + 1) * GW + (x + 1)) * 2;     // edge from pixel (x,y) to (x+1,y)
    const vId = (x, y) => ((y + 1) * GW + (x + 1)) * 2 + 1; // edge from pixel (x,y) to (x,y+1)
    const pos = (id) => { const e = id & 1, k = id >> 1, gx = (k % GW) - 1, gy = Math.floor(k / GW) - 1; return e ? [gx + 0.5, gy + 1] : [gx + 1, gy + 0.5]; };
    const adj = new Map();
    const link = (p, q) => { (adj.get(p) || adj.set(p, []).get(p)).push(q); (adj.get(q) || adj.set(q, []).get(q)).push(p); };
    for (let y = -1; y < H; y++) for (let x = -1; x < W; x++) {
      const a = at(x, y), b = at(x + 1, y), c = at(x + 1, y + 1), d = at(x, y + 1);
      const k = a | (b << 1) | (c << 2) | (d << 3);
      if (k === 0 || k === 15) continue;
      const T = hId(x, y), R = vId(x + 1, y), B = hId(x, y + 1), L = vId(x, y);
      switch (k) {
        case 1: case 14: link(L, T); break;
        case 2: case 13: link(T, R); break;
        case 4: case 11: link(R, B); break;
        case 8: case 7: link(B, L); break;
        case 3: case 12: link(L, R); break;
        case 6: case 9: link(T, B); break;
        case 5: link(L, T); link(R, B); break;   // diagonal pixels: keep them as separate shapes
        case 10: link(T, R); link(B, L); break;
      }
    }
    const loops = [], seen = new Set();
    for (const start of adj.keys()) {
      if (seen.has(start)) continue;
      const pts = []; let prev = -1, cur = start;
      while (!seen.has(cur)) {
        seen.add(cur); pts.push(pos(cur));
        const nb = adj.get(cur); const next = nb[0] !== prev ? nb[0] : nb[1];
        prev = cur; cur = next;
        if (cur === undefined) break;
      }
      if (pts.length >= 3) loops.push(smoothLoop(pts));
    }
    return { W, H, loops };
  }
  // Corner-cutting (Chaikin) twice, then drop points that add nothing visible.
  function smoothLoop(pts) {
    let P = pts;
    for (let it = 0; it < 2; it++) {
      const Q = [];
      for (let i = 0; i < P.length; i++) {
        const a = P[i], b = P[(i + 1) % P.length];
        Q.push([a[0] * 0.75 + b[0] * 0.25, a[1] * 0.75 + b[1] * 0.25], [a[0] * 0.25 + b[0] * 0.75, a[1] * 0.25 + b[1] * 0.75]);
      }
      P = Q;
    }
    // Straighten: any stretch that wobbles less than STRAIGHTEN px from a straight line becomes that line
    // (Ramer–Douglas–Peucker). This irons out the ripple left by blurry or JPEG-damaged files, giving
    // crisp straight strokes like a hand-drawn vector, while real curves keep enough points to stay round.
    const out = simplifyClosed(P, STRAIGHTEN);
    const f = new Float32Array(out.length * 2);
    out.forEach((q, i) => { f[i * 2] = q[0]; f[i * 2 + 1] = q[1]; });
    return f;
  }
  const STRAIGHTEN = 0.9; // px at working resolution (about 1/1600 of the artwork's width)
  function simplifyClosed(P, eps) {
    if (P.length < 8) return P;
    // split the loop at the point farthest from the first, then simplify each half
    let far = 0, fd = -1;
    for (let i = 1; i < P.length; i++) { const d = (P[i][0] - P[0][0]) ** 2 + (P[i][1] - P[0][1]) ** 2; if (d > fd) { fd = d; far = i; } }
    const a = rdp(P.slice(0, far + 1), eps), b = rdp(P.slice(far).concat([P[0]]), eps);
    return a.slice(0, -1).concat(b.slice(0, -1));
  }
  function rdp(pts, eps) {
    const keep = new Uint8Array(pts.length); keep[0] = keep[pts.length - 1] = 1;
    const stack = [[0, pts.length - 1]];
    while (stack.length) {
      const [s0, e0] = stack.pop(), A = pts[s0], B = pts[e0];
      const dx = B[0] - A[0], dy = B[1] - A[1], len = Math.hypot(dx, dy) || 1e-9;
      let md = -1, mi = -1;
      for (let i = s0 + 1; i < e0; i++) {
        const d = Math.abs((pts[i][0] - A[0]) * dy - (pts[i][1] - A[1]) * dx) / len;
        if (d > md) { md = d; mi = i; }
      }
      if (md > eps) { keep[mi] = 1; stack.push([s0, mi], [mi, e0]); }
    }
    return pts.filter((_, i) => keep[i]);
  }
  // Fill the outlines into a canvas region (x, y, w, h), white, even-odd so holes stay clear.
  function fillOutlines(ctx, V, x, y, w, h, color = '#fff') {
    const sx = w / V.W, sy = h / V.H, path = new Path2D();
    for (const f of V.loops) {
      path.moveTo(x + f[0] * sx, y + f[1] * sy);
      for (let i = 2; i < f.length; i += 2) path.lineTo(x + f[i] * sx, y + f[i + 1] * sy);
      path.closePath();
    }
    ctx.save(); ctx.fillStyle = color; ctx.fill(path, 'evenodd'); ctx.restore();
  }
  // The same outlines as SVG path data (for crisp display at any zoom).
  function outlinesToSvgPath(V) {
    let d = '';
    for (const f of V.loops) {
      d += `M${f[0].toFixed(1)} ${f[1].toFixed(1)}`;
      for (let i = 2; i < f.length; i += 2) d += `L${f[i].toFixed(1)} ${f[i + 1].toFixed(1)}`;
      d += 'Z';
    }
    return d;
  }

  /* ---------- output ---------- */
  function paint(mask, W, H, rgba) {
    const c = makeCanvas(W, H), x = c.getContext('2d');
    const id = x.createImageData(W, H), d = id.data;
    for (let i = 0; i < mask.length; i++) {
      if (!mask[i]) continue;
      const j = i << 2;
      d[j] = rgba[0]; d[j + 1] = rgba[1]; d[j + 2] = rgba[2]; d[j + 3] = rgba[3];
    }
    x.putImageData(id, 0, 0);
    return c;
  }

  // Production file: black = engrave, white = leave. Full engraving area at actual size.
  function production(mask, W, H) {
    const c = makeCanvas(W, H), x = c.getContext('2d');
    const id = x.createImageData(W, H), d = id.data;
    for (let i = 0; i < mask.length; i++) {
      const v = mask[i] ? 0 : 255, j = i << 2;
      d[j] = d[j + 1] = d[j + 2] = v; d[j + 3] = 255;
    }
    x.putImageData(id, 0, 0);
    return c;
  }

  function coverage(mask, W, H, rect) {
    const { x0, y0, x1, y1 } = clampRect(rect, W, H);
    let on = 0, n = 0;
    for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) { n++; on += mask[y * W + x]; }
    return n ? on / n : 0;
  }

  // Embed physical DPI (pHYs chunk) so laser software imports the file at true size.
  const CRC = (() => {
    const t = new Uint32Array(256);
    for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; }
    return t;
  })();
  function crc32(bytes) {
    let c = 0xffffffff;
    for (let i = 0; i < bytes.length; i++) c = CRC[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
    return (c ^ 0xffffffff) >>> 0;
  }
  async function canvasToPng(canvas, dpi) {
    const blob = await new Promise((r) => canvas.toBlob(r, 'image/png'));
    if (!dpi) return blob;
    const src = new Uint8Array(await blob.arrayBuffer());
    const ppm = Math.round(dpi / 0.0254);
    const chunk = new Uint8Array(21), dv = new DataView(chunk.buffer);
    dv.setUint32(0, 9);
    chunk.set([0x70, 0x48, 0x59, 0x73], 4); // "pHYs"
    dv.setUint32(8, ppm); dv.setUint32(12, ppm); chunk[16] = 1;
    dv.setUint32(17, crc32(chunk.subarray(4, 17)));
    const out = new Uint8Array(src.length + 21);
    out.set(src.subarray(0, 33)); out.set(chunk, 33); out.set(src.subarray(33), 54);
    return new Blob([out], { type: 'image/png' });
  }

  global.ClingEngrave = {
    FONTS, fontFor, makeCanvas, loadFile, configure, sourceFromImage, analyze, analyzeLogo, isolateArtwork, labelLogo, renderLogo,
    logoMask, traceMask, fillOutlines, outlinesToSvgPath,
    maskFromSource, maskFromText, maskFromMonogram, textAspect, monoAspect, bounds,
    paint, production, coverage, canvasToPng,
  };
})(window);
